import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import axios from 'axios';

// Pages
import Splash from './pages/Splash';
import Onboarding from './pages/Onboarding';
import Auth from './pages/Auth';
import ProblemSelection from './pages/ProblemSelection';
import DataInput from './pages/DataInput';
import Analysis from './pages/Analysis';
import Results from './pages/ResultsProfessional';
import NameCorrection from './pages/NameCorrection';
import MobileScore from './pages/MobileScore';
import BusinessName from './pages/BusinessName';
import WeeklyCalendar from './pages/WeeklyCalendar';
import WeeklyInsights from './pages/WeeklyInsights';
import EnergyTracker from './pages/EnergyTracker';
import History from './pages/History';
import Dashboard from './pages/Dashboard';
import LoveCompatibility from './pages/LoveCompatibility';
import VastuDashboard from './pages/VastuDashboard';
import AstrologyDashboard from './pages/AstrologyDashboard';
import HomePage from './pages/HomePage';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentWelcome from './pages/PaymentWelcome';
import CheckoutPage from './pages/CheckoutPage';
import ProfileManagement from './pages/ProfileManagement';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RefundPolicy from './pages/RefundPolicy';
import TermsConditions from './pages/TermsConditions';
import Disclaimer from './pages/Disclaimer';
import ContactUs from './pages/ContactUs';
import AdminConsole from './pages/AdminConsole';
import AdminLogin from './pages/AdminLogin';

// Configure axios
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
axios.defaults.baseURL = API_URL;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user has token
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
    }

    // Check if user has seen onboarding
    const onboarded = localStorage.getItem('onboarded');
    if (onboarded) {
      setHasSeenOnboarding(true);
    }

    setLoading(false);
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
  };

  const completeOnboarding = () => {
    localStorage.setItem('onboarded', 'true');
    setHasSeenOnboarding(true);
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-welcome" element={<PaymentWelcome />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/contact" element={<ContactUs />} />

          {/* Splash - Remove since we have homepage */}
          {/* <Route path="/" element={<Splash />} /> */}

          {/* Onboarding */}
          <Route
            path="/onboarding"
            element={<Onboarding onComplete={completeOnboarding} />}
          />

          {/* Auth */}
          <Route
            path="/auth"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                <Auth onLogin={handleLogin} />
              )
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                <Dashboard onLogout={handleLogout} />
              ) : (
                <Navigate to="/auth" />
              )
            }
          />
          <Route
            path="/problem-selection"
            element={
              isAuthenticated ? <ProblemSelection /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/data-input"
            element={
              isAuthenticated ? <DataInput /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/analysis"
            element={
              isAuthenticated ? <Analysis /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/results"
            element={
              isAuthenticated ? <Results /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/name-correction"
            element={
              isAuthenticated ? <NameCorrection /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/mobile-score"
            element={
              isAuthenticated ? <MobileScore /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/business-name"
            element={
              isAuthenticated ? <BusinessName /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/weekly-calendar"
            element={
              isAuthenticated ? <WeeklyCalendar /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/weekly-insights"
            element={
              isAuthenticated ? <WeeklyInsights /> : <Navigate to="/auth" />
            }
          />

          <Route
            path="/energy-tracker"
            element={
              isAuthenticated ? <EnergyTracker /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/history"
            element={
              isAuthenticated ? <History /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/love-compatibility"
            element={
              isAuthenticated ? <LoveCompatibility /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/vastu"
            element={
              isAuthenticated ? <VastuDashboard /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/astrology"
            element={
              isAuthenticated ? <AstrologyDashboard /> : <Navigate to="/auth" />
            }
          />
          <Route
            path="/profile"
            element={
              isAuthenticated ? <ProfileManagement /> : <Navigate to="/auth" />
            }
          />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminConsole />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
